import { useEffect, useMemo, useState } from "react";
import type { BonusOverview } from "../../../server/types.ts";
import { api, errorMessage } from "../api.ts";
import { Icon } from "../components/icons.tsx";
import { Badge, Empty, Note, Spinner, Stat } from "../components/ui.tsx";
import { maskedCard, relativeTime } from "../format.ts";
import { useApi } from "../hooks.ts";

export function Offers() {
	const loaded = useApi((signal) => api.bonusOverview(false, { signal }), []);
	const [overview, setOverview] = useState<BonusOverview | null>(null);
	const [selected, setSelected] = useState<string | null>(null);
	const [refreshing, setRefreshing] = useState(false);
	const [refreshError, setRefreshError] = useState<string | null>(null);

	useEffect(() => {
		if (loaded.data !== null) setOverview(loaded.data);
	}, [loaded.data]);

	const categories = useMemo(
		() => overview?.categories.filter((category) => selected === null || category.id === selected) ?? [],
		[overview, selected],
	);

	const refresh = async (): Promise<void> => {
		setRefreshing(true);
		setRefreshError(null);
		try {
			setOverview(await api.bonusOverview(true));
		} catch (error) {
			setRefreshError(errorMessage(error));
		} finally {
			setRefreshing(false);
		}
	};

	return (
		<main className="screen stack offers-screen">
			<header className="screen-title offers-title">
				<div>
					<p className="eyebrow">ALL OWNED CARDS</p>
					<h1>Bonus box overview</h1>
					<p>Identical offers are merged; open one to see every card it appears on.</p>
				</div>
				<button type="button" className="btn btn-quiet" disabled={refreshing} onClick={() => void refresh()}>
					<Icon name="refresh" /> {refreshing ? "Refreshing…" : "Refresh boxes"}
				</button>
			</header>

			{loaded.loading && overview === null && <Spinner label="Reading every cached bonus box…" />}
			{loaded.error !== null && overview === null && <Note tone="danger">{loaded.error}</Note>}
			{refreshError !== null && <Note tone="danger">{refreshError}</Note>}

			{overview !== null && (
				<>
					<section className="stats-grid overview-stats" aria-label="Overview totals">
						<Stat label="UNIQUE" value={overview.uniqueOfferCount} hint={`${overview.rawOfferCount} card offers`} />
						<Stat label="MERGED" value={overview.duplicatesMerged} hint="duplicate rows removed" />
						<Stat label="CARDS" value={overview.boxesLoaded} hint={`${overview.ownedAccounts} owned`} />
					</section>

					{overview.boxesMissing > 0 && (
						<Note tone="warn">
							{overview.boxesMissing} owned card{overview.boxesMissing === 1 ? " has" : "s have"} no box for this week.
							 Refresh to try them now.
						</Note>
					)}

					<div className="offer-filter" aria-label="AH merchandise category">
						<button type="button" className={selected === null ? "chip chip-on" : "chip"} onClick={() => setSelected(null)}>
							All <span>{overview.uniqueOfferCount}</span>
						</button>
						{overview.categories.map((category) => (
							<button
								type="button"
								key={category.id}
								className={selected === category.id ? "chip chip-on" : "chip"}
								onClick={() => setSelected(category.id)}
							>
								{category.title} <span>{category.offerCount}</span>
							</button>
						))}
					</div>

					{categories.length === 0 ? (
						<Empty title="No personal offers yet">
							The owned cards do not have a filled Bonus Box for this week. Use Refresh boxes after AH has filled them.
						</Empty>
					) : (
						<div className="offer-categories">
							{categories.map((category) => (
								<section className="offer-category" key={category.id}>
									<header>
										<div><p className="eyebrow">AH CATEGORY</p><h2>{category.title}</h2></div>
										<Badge tone="neutral">{category.offerCount} offer{category.offerCount === 1 ? "" : "s"}</Badge>
									</header>
									<ul className="merged-offers">
										{category.offers.map((offer) => (
											<li className="merged-offer" key={offer.hqId}>
												<div className="offer-picture">
													{offer.imageUrl ? <img src={offer.imageUrl} alt="" loading="lazy" /> : <Icon name="gift" />}
												</div>
												<div className="offer-copy">
													<div className="offer-badges">
														<Badge tone="money">{offer.mechanicLabel}</Badge>
														<Badge tone={offer.cardCount > 1 ? "accent" : "neutral"}>
															{offer.cardCount} card{offer.cardCount === 1 ? "" : "s"}
														</Badge>
													</div>
													<h3>{offer.title}</h3>
													{offer.subtitle && <p>{offer.subtitle}</p>}
													<span className="offer-meta">
														{offer.productCount} product{offer.productCount === 1 ? "" : "s"}
														{offer.activatedCount > 0 && ` · active on ${offer.activatedCount}`}
													</span>
													<details className="offer-cards">
														<summary>Cards carrying this offer</summary>
														<ul>
															{offer.cards.map((card) => (
																<li key={card.accountId}>
																	<span><b>{maskedCard(card.bonusCard)}</b><small>{card.email}</small></span>
																	<Badge tone={card.activationStatus === "ACTIVATED" ? "money" : "neutral"}>
																		{card.activationStatus === "ACTIVATED" ? "active" : "available"}
																	</Badge>
																</li>
															))}
														</ul>
													</details>
												</div>
											</li>
										))}
									</ul>
								</section>
							))}
						</div>
					)}

					<p className="overview-age">
						Generated {relativeTime(overview.generatedAt, Date.now())} · week {overview.week.startDate}–{overview.week.endDate}
					</p>
				</>
			)}
		</main>
	);
}
